import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { exerciseCatalogService } from '../services/exerciseCatalog';
import { ExerciseCard } from './ExerciseCard';
import { ExerciseCategorySelector } from './ExerciseCategorySelector';
import type { Exercise, ExerciseSearchResult } from '../types';

interface ExerciseSelectorProps {
  onExerciseSelect: (exercise: Exercise) => void;
  selectedExercises?: Exercise[];
  multiSelect?: boolean;
  showDetails?: boolean;
  className?: string;
  placeholder?: string;
}

/**
 * ExerciseSelector component with search and filtering capabilities
 * Supports Russian text search, muscle group filtering, and multi-selection
 */

// Category to muscle group mapping
const CATEGORY_MUSCLE_MAPPING: Record<string, string[]> = {
  'спина': ['спина', 'широчайшие', 'средняя трапеция', 'верхняя трапеция', 'нижняя трапеция'],
  'грудь': ['грудь', 'верх груди', 'низ груди', 'средняя часть груди'],
  'ноги': ['ноги', 'квадрицепс', 'бицепс бедра', 'икры', 'камбаловидная'],
  'плечи': ['плечи', 'передние дельты', 'средние дельты', 'задние дельты'],
  'ягодицы': ['ягодицы'],
  'бицепс': ['бицепс'],
  'трицепс': ['трицепс'],
  'предплечья': ['предплечья']
};

export const ExerciseSelector: React.FC<ExerciseSelectorProps> = ({
  onExerciseSelect,
  selectedExercises = [],
  multiSelect = false,
  showDetails = false,
  className = '',
  placeholder = 'Поиск упражнений...'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState<string>('');
  const [searchResult, setSearchResult] = useState<ExerciseSearchResult>({
    exercises: [],
    total_count: 0,
    has_more: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [showCategories, setShowCategories] = useState(true);
  const [isMultiMuscleSearch, setIsMultiMuscleSearch] = useState(false);
  const [cachedCategoryExercises, setCachedCategoryExercises] = useState<Exercise[]>([]);

  // Create a set of selected exercise IDs for quick lookup
  const selectedExerciseIds = useMemo(() => 
    new Set(selectedExercises.map(ex => ex.id)), 
    [selectedExercises]
  );

  // Debounced search function
  const performSearch = useCallback(async (
    query: string, 
    muscleGroup: string, 
    offset: number = 0,
    append: boolean = false
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await exerciseCatalogService.searchExercises({
        query: query.trim(),
        muscle_group: muscleGroup || undefined,
        limit: 20,
        offset
      });

      setSearchResult(prevResult => ({
        ...result,
        exercises: append ? [...prevResult.exercises, ...result.exercises] : result.exercises
      }));
      
      setCurrentOffset(offset);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Ошибка поиска упражнений');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Search with multiple muscle groups (for category mapping)
  const performSearchWithMultipleMuscles = useCallback(async (
    muscleGroups: string[]
  ) => {
    setIsLoading(true);
    setError(null);
    setIsMultiMuscleSearch(true);

    console.log('🔍 performSearchWithMultipleMuscles called with:', muscleGroups);

    try {
      // Search for each muscle group and combine results
      console.log('🔍 Starting parallel searches for', muscleGroups.length, 'muscle groups');
      
      const allResults = await Promise.all(
        muscleGroups.map(async (muscle, index) => {
          console.log(`🔍 [${index + 1}/${muscleGroups.length}] Searching for:`, muscle);
          const result = await exerciseCatalogService.searchExercises({
            muscle_group: muscle,
            limit: 100
          });
          console.log(`✅ [${index + 1}/${muscleGroups.length}] Found ${result.exercises.length} exercises for "${muscle}"`);
          return result;
        })
      );

      console.log('🔍 All searches completed. Results:', allResults.map((r, i) => ({
        muscle: muscleGroups[i],
        count: r.exercises.length
      })));

      // Combine and deduplicate exercises
      const exerciseMap = new Map();
      allResults.forEach((result, index) => {
        console.log(`🔍 Processing result ${index + 1}: ${result.exercises.length} exercises`);
        result.exercises.forEach(exercise => {
          if (!exerciseMap.has(exercise.id)) {
            exerciseMap.set(exercise.id, exercise);
          }
        });
      });

      const combinedExercises = Array.from(exerciseMap.values());
      
      console.log('✅ Final combined result:', combinedExercises.length, 'unique exercises');
      console.log('📋 Exercise IDs:', combinedExercises.slice(0, 10).map(ex => ({ id: ex.id, name: ex.name_ru })));
      
      // Cache the category exercises for local filtering
      setCachedCategoryExercises(combinedExercises);
      
      setSearchResult({
        exercises: combinedExercises,
        total_count: combinedExercises.length,
        has_more: false
      });
      
      console.log('✅ State updated with', combinedExercises.length, 'exercises');
      
      setCurrentOffset(0);
    } catch (err) {
      console.error('❌ Search failed:', err);
      setError('Ошибка поиска упражнений');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    // Skip if using multi-muscle search (category with multiple muscle groups)
    if (!showCategories && !isMultiMuscleSearch) {
      const timeoutId = setTimeout(() => {
        performSearch(searchQuery, selectedMuscleGroup, 0, false);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, selectedMuscleGroup, performSearch, showCategories, isMultiMuscleSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    
    // If we have cached category exercises (multi-muscle search), filter locally
    if (isMultiMuscleSearch && cachedCategoryExercises.length > 0) {
      if (newValue.trim() === '') {
        // Show all cached exercises
        setSearchResult({
          exercises: cachedCategoryExercises,
          total_count: cachedCategoryExercises.length,
          has_more: false
        });
      } else {
        // Filter cached exercises locally
        const normalizedQuery = newValue.trim().toLowerCase();
        const filtered = cachedCategoryExercises.filter(exercise =>
          exercise.name_ru.toLowerCase().includes(normalizedQuery) ||
          exercise.name_en?.toLowerCase().includes(normalizedQuery) ||
          exercise.description?.toLowerCase().includes(normalizedQuery)
        );
        
        setSearchResult({
          exercises: filtered,
          total_count: filtered.length,
          has_more: false
        });
      }
    } else {
      // Regular search mode - let useEffect handle it
      if (newValue.trim() === '') {
        const muscleVariations = CATEGORY_MUSCLE_MAPPING[selectedMuscleGroup] || [];
        
        if (muscleVariations.length > 1) {
          setIsMultiMuscleSearch(true);
          performSearchWithMultipleMuscles(muscleVariations);
        } else {
          setIsMultiMuscleSearch(false);
        }
      } else {
        setIsMultiMuscleSearch(false);
      }
    }
  };

  const handleExerciseSelect = (exercise: Exercise) => {
    onExerciseSelect(exercise);
  };

  const handleLoadMore = () => {
    if (searchResult.has_more && !isLoading) {
      const newOffset = currentOffset + 20;
      performSearch(searchQuery, selectedMuscleGroup, newOffset, true);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
  };

  const handleCategorySelect = (category: string) => {
    console.log('🎯 Category selected:', category);
    
    setSelectedMuscleGroup(category);
    setShowCategories(false);
    
    // Get all muscle variations for this category
    const muscleVariations = CATEGORY_MUSCLE_MAPPING[category] || [category];
    
    console.log('🗺️ Mapped to muscle variations:', muscleVariations);
    
    // If category has multiple muscle variations, use multi-muscle search
    if (muscleVariations.length > 1) {
      performSearchWithMultipleMuscles(muscleVariations);
    } else {
      // Single muscle or "all exercises" - use regular search
      performSearch('', category, 0, false);
    }
  };

  const handleBackToCategories = () => {
    setShowCategories(true);
    setSearchQuery('');
    setSelectedMuscleGroup('');
    setIsMultiMuscleSearch(false);
    setCachedCategoryExercises([]);
    setSearchResult({
      exercises: [],
      total_count: 0,
      has_more: false
    });
  };

  // Показываем категории, если пользователь еще не выбрал категорию
  if (showCategories) {
    return (
      <div className={`exercise-selector ${className}`}>
        <ExerciseCategorySelector onCategorySelect={handleCategorySelect} />
      </div>
    );
  }

  return (
    <div className={`exercise-selector ${className}`}>
      {/* Back to Categories Button */}
      <div className="mb-4">
        <button
          onClick={handleBackToCategories}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Назад к категориям
        </button>
      </div>

      {/* Current Category Display */}
      {selectedMuscleGroup && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm font-medium text-blue-800">
            Категория: <span className="capitalize">{selectedMuscleGroup}</span>
          </p>
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-4">
        {/* Search Input */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={placeholder}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Clear Filters Button */}
        {searchQuery && (
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Очистить поиск
          </button>
        )}

        {/* Results Summary */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {isLoading ? 'Поиск...' : `Найдено: ${searchResult.total_count} упражнений`}
          </span>
          {multiSelect && selectedExercises.length > 0 && (
            <span className="font-medium">
              Выбрано: {selectedExercises.length}
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Exercise List */}
      <div className="space-y-3">
        {searchResult.exercises.length === 0 && !isLoading ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.562M15 6.306a7.962 7.962 0 00-6 0m6 0V5a2 2 0 00-2-2H9a2 2 0 00-2 2v1.306m8 0V7a2 2 0 012 2v6.414l-1.293-1.293a1 1 0 00-1.414 0L12 16.414l-2.293-2.293a1 1 0 00-1.414 0L7 15.414V9a2 2 0 012-2h8a2 2 0 012 2v6.414z" />
            </svg>
            <p>Упражнения не найдены</p>
            <p className="text-sm mt-1">Попробуйте изменить параметры поиска</p>
          </div>
        ) : (
          <>
            {searchResult.exercises.map(exercise => (
              <ExerciseCard
                key={exercise.id}
                exercise={exercise}
                onSelect={handleExerciseSelect}
                isSelected={selectedExerciseIds.has(exercise.id)}
                showDetails={showDetails}
              />
            ))}

            {/* Load More Button */}
            {searchResult.has_more && (
              <div className="text-center pt-4">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="px-6 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Загрузка...' : 'Загрузить ещё'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Loading Indicator */}
      {isLoading && searchResult.exercises.length === 0 && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}
    </div>
  );
};

export default ExerciseSelector;